'use client'

import { useRef } from 'react'
import type { HeroSplitFragment } from '@/lib/types/datocms/graphql/generated'
import { TextWithHighlight } from '../TextWithHighlight'
import { StructuredText } from 'react-datocms'
import Eyebrow from '../ui/Eyebrow'
import CtaDefault from '../ui/cta/CtaDefault'
import ScrollDown from '../ui/ScrollDown'
import CircleGraphic from '../ui/CircleGraphic'
import Breadcrumbs from '../Breadcrumbs'
import Image from 'next/image'
import clsx from 'clsx'
import { usePathname } from 'next/navigation'

export default function HeroSplit({
	title,
	image,
	tag,
	description,
	cta,
	align,
	backgroundColor,
}: Readonly<HeroSplitFragment>) {
	const ref = useRef<HTMLDivElement>(null)
	const pathname = usePathname()

	return (
		<section className="hero-split @container">
			<div
				ref={ref}
				className={clsx(
					'flex max-lg:flex-col @5xl:justify-between mx-auto mt-[--nav-height] w-full',
					align === 'right' ? '' : '@5xl:flex-row-reverse',
					backgroundColor === 'blue' ? 'bg-blue' : 'bg-white-frosted'
				)}
			>
				{/* Text Side */}
				<div
					className={clsx(
						'flex flex-col justify-between min-h-[390px] @5xl:min-h-[calc(90vh-var(--nav-height))] w-full h-full @5xl:w-1/2 common-x-padding @5xl:px-0',
						align === 'right' ? '@5xl:common-left-padding' : '@5xl:common-right-padding'
					)}
				>
					<Breadcrumbs
						theme={backgroundColor === 'blue' ? 'dark' : 'light'}
						breadcrumbs={[{ label: pathname?.split('/')[1]?.replace(/-/g, ' ') }]}
						className={clsx(align === 'left' ? 'common-left-padding' : 'common-right-padding')}
					/>
					<div className="@5xl:w-[85%] max-w-xl @5xl:mx-auto py-20">
						{/* Eyebrow */}
						{tag ? (
							<Eyebrow variant={backgroundColor === 'blue' ? 'secondary' : 'primary'}>
								{tag}
							</Eyebrow>
						) : null}
						{/* Title */}
						{title ? (
							<h1
								className={clsx(
									'heading-size-1 z-10 max-w-3xl @5xl:max-w-5xl mt-4 @5xl:mt-6',
									backgroundColor === 'blue' ? 'text-white' : 'text-blue-dark'
								)}
							>
								<TextWithHighlight text={title} />
							</h1>
						) : null}

						{description ? (
							<div
								className={clsx(
									backgroundColor === 'blue' ? 'text-white' : 'text-contrast',
									'text-lg mt-6 @5xl:mt-5 text-balance'
								)}
							>
								<StructuredText data={description} />
							</div>
						) : null}

						{cta?.label ? (
							<CtaDefault
								cta={cta}
								variant={backgroundColor === 'blue' ? 'secondary' : 'primary'}
								className="mt-10 hidden @5xl:flex"
							/>
						) : null}
					</div>
					<div className="@5xl:w-[85%] @5xl:mx-auto max-w-xl">
						<ScrollDown
							sectionRef={ref}
							variant={backgroundColor === 'blue' ? 'primary' : 'tertiary'}
							arrowAlign="left"
							className="mb-8"
						>
							Discover more
						</ScrollDown>
					</div>
				</div>

				{/* Image Side */}
				<div className="flex flex-col @5xl:w-1/2 bg-blue-dark">
					<div className="relative h-full w-full aspect-square sm:aspect-video">
						<Image
							src={image?.[0]?.url}
							alt={image?.[0]?.alt ?? 'Hero Image'}
							fill
							className="object-cover"
							sizes="(max-width: 768px) 100vw, 50vw"
							priority
						/>
					</div>

					<div className="h-[80px] @5xl:h-[195px] w-full bg-blue-dark overflow-hidden">
						<div className="relative mx-auto overflow-hidden">
							<div className="flex flex-wrap gap-4 @5xl:gap-3 @5xl:gap-y-12shrink-0 w-[120%] translate-x-1 translate-y-2 @5xl:translate-y-2.5 @5xl:translate-x-3">
								{Array.from({ length: 50 }).map((_, index) => (
									<CircleGraphic
										key={`shape-${index + 1}`}
										size="sm"
										theme="blue"
										className="shrink-0"
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
